import React from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { ClickableLinkText } from '../Link';
import Text, { TextProps } from '../Text';
import Stack from '../Stack';
import Divider from '../Divider';
import SkeletonLoadingContainer from '../SkeletonLoadingContainer';
import { getSpacing } from '../../../utils/spacing';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import _chunk from 'lodash/chunk';

export type Value = string | number | boolean | null | undefined | React.ReactNode;

export type LabeledValueVariant = 'stacked' | 'spaced';

export type LinkType = 'url' | 'email' | 'phone' | 'address';

export type LabelValueItem = {
    label: string;
    value: Value;
    testID?: string;
    linkType?: LinkType;
    isLoading?: boolean;
};

export type LabelValueDisplayOwnProps = {
    items: LabelValueItem[];
    style?: StyleProp<ViewStyle>;
    omitEmptyValues?: boolean;
    headerTitle?: string;
    actionItem?: React.ReactElement;
    variant?: LabeledValueVariant;
};

export type LabelValueDisplayProps = LabelValueDisplayOwnProps;

const LabelValueDisplay: React.FC<LabelValueDisplayProps> = (props) => {
    const isMobile = useIsMobileLayout();
    const variant = props.variant || 'stacked';
    const valuesToDisplay = (props.items || [])
        .map(({ label, value, ...rest }) => {
            const isNullishValue = value === null || value === undefined || value === '';

            if (props.omitEmptyValues && isNullishValue) return null;

            return { label, value: isNullishValue ? '' : value, ...rest };
        })
        .filter(Boolean) as LabelValueItem[];

    const renderHeader = () => {
        if (!props.headerTitle && !props.actionItem) return null;

        return (
            <Stack horizontal fullWidth align="center" justify="space-between">
                <Text variant="h5">{props.headerTitle || ''}</Text>
                {props.actionItem || ''}
            </Stack>
        );
    };

    const renderValue = (
        textProps: Omit<TextProps, 'children'>,
        value: Value,
        linkType: LinkType | undefined,
        label: string,
    ) => {
        const testID = `${label}-value`;

        const renderText = (text: string) => (
            <Text {...textProps} testID={testID}>
                {text}
            </Text>
        );

        const renderLink = (text: string, linkType: LinkType) => (
            <ClickableLinkText linkType={linkType} {...textProps} testID={testID}>
                {text}
            </ClickableLinkText>
        );

        if (value === null || value === undefined) {
            return null;
        } else if (linkType !== undefined && typeof value === 'string') {
            return renderLink(value, linkType);
        } else if (['string', 'number', 'boolean'].includes(typeof value)) {
            return renderText(String(value));
        } else {
            return value;
        }
    };

    const getRootTestId = (label: string) => `labeled-value-${label}`;

    const renderItem = (item: LabelValueItem) => (
        <Stack
            vertical
            testID={getRootTestId(item.label)}
            align="flex-start"
            fullWidth
            style={{
                paddingTop: getSpacing(1),
                paddingBottom: getSpacing(1),
            }}
            space={2}
        >
            <Text variant="labeled-value-label">{item.label}</Text>
            {renderValue(
                {
                    variant: 'labeled-value-value',
                },
                item.value,
                item.linkType,
                item.label,
            )}
            <Divider
                horizontal
                style={{
                    marginTop: 1,
                }}
            />
        </Stack>
    );

    if (variant === 'stacked') {
        return (
            <Stack vertical style={props.style} space={0} fullWidth>
                {renderHeader()}
                {valuesToDisplay.map((item, index) => (
                    <SkeletonLoadingContainer
                        isLoading={!!item.isLoading}
                        key={index + item.label}
                        skeletonStyle={{ width: '100%' }}
                    >
                        <Stack vertical fullWidth>
                            {renderItem(item)}
                        </Stack>
                    </SkeletonLoadingContainer>
                ))}
            </Stack>
        );
    } else {
        const chunkSize = isMobile ? 2 : 4;
        const chunkedItems = _chunk(valuesToDisplay, chunkSize).map<(LabelValueItem | null)[]>((_) => {
            if (_.length < chunkSize) {
                return _.concat(Array(chunkSize - _.length).fill(null));
            }
            return _;
        });

        return (
            <Stack vertical fullWidth space={16}>
                <Divider horizontal />
                {chunkedItems.map((chunk, index) => (
                    <Stack horizontal fullWidth key={index} space={16}>
                        {chunk.map((item, index) => (
                            <Stack vertical key={index} flex={1} justify="flex-start" align="flex-start">
                                {item ? (
                                    <>
                                        <Text variant="caption-1">{item.label}</Text>
                                        {renderValue(
                                            {
                                                variant: 'body-1',
                                            },
                                            item.value,
                                            item.linkType,
                                            item.label,
                                        )}
                                    </>
                                ) : null}
                            </Stack>
                        ))}
                    </Stack>
                ))}
                <Divider horizontal />
            </Stack>
        );
    }
};

export default React.memo(LabelValueDisplay);
